import React, { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import MultiSelectFilter from "./MultiSelectFilter";
import CustomTable from "components/Table/CustomTable";
import { Dropdown, DropdownToggle, DropdownMenu, Badge } from "reactstrap";

const MultiSelect = ({ placeholder = "เลือกรายการ", filters = [], table, initData = [], maxVisibleTags = 3, onSearch, onFilter, onChange }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterQuery, setFilterQuery] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const tableContainerRef = useRef(null);
  const scrollPositionRef = useRef(0);

  const keyField = table?.keyField || "id";
  const keyName = table?.keyName || "name";

  useEffect(() => {
    if (initData && initData.length > 0) {
      setSelectedItems(initData);
    }
  }, [initData, table?.data]);

  // Save scroll position before state change
  const saveScrollPosition = () => {
    if (tableContainerRef.current) {
      const tableElement = tableContainerRef.current.querySelector('[style*="max-height"]');
      if (tableElement) {
        scrollPositionRef.current = tableElement.scrollTop;
      }
    }
  };

  // Restore scroll position after state change
  const restoreScrollPosition = () => {
    if (tableContainerRef.current && scrollPositionRef.current > 0) {
      requestAnimationFrame(() => {
        const tableElement = tableContainerRef.current.querySelector('[style*="max-height"]');
        if (tableElement) {
          tableElement.scrollTop = scrollPositionRef.current;
        }
      });
    }
  };

  const onChangeFilter = (filter) => {
    saveScrollPosition();
    setFilterQuery(filter);
    setTimeout(restoreScrollPosition, 0);
  };

  const handleSelectionChange = (selected) => {
    saveScrollPosition();
    setSelectedItems(selected);
    if (onChange) {
      onChange(selected);
    }
    setTimeout(restoreScrollPosition, 0);
  };

  const handleRemoveTag = (itemId, event) => {
    event.stopPropagation();
    saveScrollPosition();
    const newSelectedItems = selectedItems.filter((item) => item[keyField] !== itemId);
    setSelectedItems(newSelectedItems);
    if (onChange) {
      onChange(newSelectedItems);
    }
    setTimeout(restoreScrollPosition, 0);
  };

  const renderTags = () => {
    if (selectedItems.length === 0) return placeholder;

    if (selectedItems.length <= maxVisibleTags) {
      return (
        <div className="selected-tags d-flex flex-wrap gap-2">
          {selectedItems.map((item) => (
            <Badge key={item[keyField]} color="light" className="d-flex align-items-center gap-2">
              {item[keyName]}
              <i className="bx bx-x cursor-pointer" onClick={(e) => handleRemoveTag(item[keyField], e)} />
            </Badge>
          ))}
        </div>
      );
    }

    return (
      <div className="selected-tags d-flex flex-wrap gap-2">
        {selectedItems.slice(0, 1).map((item) => (
          <Badge key={item[keyField]} color="light" className="d-flex align-items-center gap-2">
            {item[keyName]}
            <i className="bx bx-x cursor-pointer" onClick={(e) => handleRemoveTag(item[keyField], e)} />
          </Badge>
        ))}
        <Badge color="light">...{selectedItems.length - 2} รายการ...</Badge>
        {selectedItems.slice(-1).map((item) => (
          <Badge key={item[keyField]} color="light" className="d-flex align-items-center gap-2">
            {item[keyName]}
            <i className="bx bx-x cursor-pointer" onClick={(e) => handleRemoveTag(item[keyField], e)} />
          </Badge>
        ))}
      </div>
    );
  };

  const filteredData = useMemo(() => {
    if (!table?.data) {
      console.log("No table data available");
      return [];
    }

    if (onSearch || onFilter) {
      return table.data;
    }

    if (!filterQuery || filterQuery.length === 0) {
      return table.data;
    }

    return table.data.filter((item) => {
      return filterQuery.every((filter) => {
        if (!filter.data || filter.data.length === 0) return true;

        return filter.data.some((filterItem) => {
          const accessorParts = filter.accessor.split(".");
          let itemValue = item;

          for (const part of accessorParts) {
            if (itemValue && typeof itemValue === "object") {
              itemValue = itemValue[part];
            } else {
              itemValue = undefined;
              break;
            }
          }

          return filterItem.name === itemValue;
        });
      });
    });
  }, [filterQuery, table?.data, onSearch, onFilter]);

  return (
    <div className="multi-select-with-table">
      <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen((prevState) => !prevState)} className="w-100 fit-content">
        <DropdownToggle caret className="w-100 text-start d-flex align-items-center text-gray-400">
          <i className="bx bx-search mr-2" />
          {renderTags()}
        </DropdownToggle>
        <DropdownMenu className="w-100 p-3" style={{ maxHeight: 600 }}>
          <div className="mb-3">
            <MultiSelectFilter filters={filters} onChange={onChangeFilter} />
            <div>ค้นพบรายการ Product Affiliate ในผลิตภัณฑ์ ทั้งหมด {filteredData.length} รายการ</div>
          </div>
          <div ref={tableContainerRef}>{filteredData.length > 0 ? <CustomTable keyField={keyField} data={filteredData} columns={table.columns} selectable={true} initSelectedRows={selectedItems.map((item) => item[keyField])} maxHeight={256} onSelectionChange={handleSelectionChange} displayFooter={false} /> : <div className="text-center py-4">ไม่พบข้อมูลสินค้า</div>}</div>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

MultiSelect.propTypes = {
  table: PropTypes.shape({
    keyField: PropTypes.string,
    keyName: PropTypes.string,
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
  }),
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      accessor: PropTypes.string.isRequired,
      data: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          name: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  initData: PropTypes.array,
  onChange: PropTypes.func,
  onSearch: PropTypes.func,
  onFilter: PropTypes.func,
};

export default MultiSelect;
